import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DownloadAppStore, DownloadPlayStore, Headline, Subheading } from 'src/components';
import QRPlayStore from 'src/components/QRPlayStore';
import QRAppStore from 'src/components/QRAppStore';
import { IS_WEB, PETPAGE_IOS_APP_STORE, PETPAGE_PLAY_STORE, Size } from 'src/constants';
import { useAppTheme } from 'src/providers/AppThemeProvider';
import { Pressable } from 'react-native';
import { openWebBrowser } from 'src/utils';
import DeviceInfo from 'react-native-device-info';
import styled from 'styled-components/native';
import { FontSize } from 'src/theme';

const PLAY_STORE_BADGE_WIDTH = 195;
const APP_STORE_BADGE_WIDTH = 160;
const PLAY_STORE_BADGE_WIDTH_MOBILE = 163;
const APP_STORE_BADGE_WIDTH_MOBILE = 128;
const QR_SIDE_LENGTH = 195;

interface Props {
  justify?: 'center' | 'flex-start' | 'flex-end' | 'space-between';
  headerTextAlign?: 'justify' | 'center' | 'left' | 'right' | 'auto';
  fontSize?: number;
}

const DownloadOurApp = ({ justify, headerTextAlign, fontSize }: Props) => {
  const { t } = useTranslation('login');
  const { viewMode, colors } = useAppTheme();

  const [baseOs, setBaseOs] = useState<string>();

  useEffect(() => {
    if (!baseOs) {
      void DeviceInfo.getBaseOs().then((os) => {
        setBaseOs(os);
      });
    }
  }, [baseOs]);

  if (!baseOs) return null;

  const showAppStoreBadge = IS_WEB || baseOs !== 'Android';
  const showPlayStoreBadge = IS_WEB || baseOs === 'Android';

  return (
    <DownloadOurAppContainer>
      <Header
        textAlign={headerTextAlign ?? 'left'}
        color={viewMode.isWeb ? colors.onSurface : colors.onPrimary}
        fontSize={fontSize ?? FontSize.HEADLINE}
      >
        {t('downloadTitle')}
      </Header>
      {viewMode.isWeb && (
        <Subheading
          textAlign={headerTextAlign ?? 'left'}
          color={viewMode.isWeb ? colors.onSurface : colors.onPrimary}
        >
          {t('downloadSubtitle')}
        </Subheading>
      )}
      <AppStoreContentContainer justify={justify}>
        {showAppStoreBadge && (
          <AppStoreContentInnerContainer>
            {viewMode.isWeb && <QRAppStore size={QR_SIDE_LENGTH} />}
            <AppStoreBadgeContainer>
              <Pressable onPress={() => openWebBrowser(PETPAGE_IOS_APP_STORE)}>
                <DownloadAppStore
                  width={viewMode.isMobile ? APP_STORE_BADGE_WIDTH_MOBILE : APP_STORE_BADGE_WIDTH}
                />
              </Pressable>
            </AppStoreBadgeContainer>
          </AppStoreContentInnerContainer>
        )}
        {showPlayStoreBadge && (
          <AppStoreContentInnerContainer>
            {viewMode.isWeb && <QRPlayStore size={QR_SIDE_LENGTH} />}
            <Pressable onPress={() => openWebBrowser(PETPAGE_PLAY_STORE)}>
              <DownloadPlayStore
                width={viewMode.isMobile ? PLAY_STORE_BADGE_WIDTH_MOBILE : PLAY_STORE_BADGE_WIDTH}
              />
            </Pressable>
          </AppStoreContentInnerContainer>
        )}
      </AppStoreContentContainer>
    </DownloadOurAppContainer>
  );
};

export default DownloadOurApp;

const DownloadOurAppContainer = styled.ScrollView`
  ${({ theme }) => theme.viewMode.isWeb && `flex: 1;`}
  ${({ theme }) => theme.viewMode.isWeb && `background-color: ${theme.colors.surface}`};
  ${({ theme }) => theme.viewMode.isWeb && `padding-horizontal: ${Size.M}px;`}
  gap: ${Size.X2_S}px;
  padding-vertical: ${Size.S}px;
`;

const Header = styled(Headline)`
  ${({ theme }) => !theme.viewMode.isWeb && `margin-left: ${Size.XS}px;`}
`;

const AppStoreContentContainer = styled.View<{ justify?: string }>`
  ${({ theme }) => theme.viewMode.isWeb && `padding-top: ${Size.S}px;`}
  flex-direction: row;
  justify-content: ${({ justify }) => justify ?? 'space-between'};
`;

const AppStoreContentInnerContainer = styled.View`
  align-items: flex-start;
`;

const AppStoreBadgeContainer = styled.View`
  ${({ theme }) => !theme.viewMode.isWeb && `margin-left: ${Size.XS}px;`}
  padding-left: ${({ theme }) => (theme.viewMode.isWeb ? Size.S : 0)}px;
  padding-top: ${Size.XS}px;
`;
